// components/schedule/CrewMemberFilter.js
import React from 'react';
import { useTranslations } from '../../hooks/useTranslations';

export function CrewMemberFilter({
  selectedCrewMember,
  onCrewMemberChange,
  crewMembers = [],
  className = '',
}) {
  const t = useTranslations();

  return (
    <div className={`w-full ${className}`}>
      {/* Tab-style design for all screen sizes */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide">
        <button
          onClick={() => onCrewMemberChange('all')}
          className={`px-1 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
            selectedCrewMember === 'all'
              ? 'text-pink-600 border-pink-600'
              : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
          }`}
        >
          {t.allMembers}
        </button>

        {crewMembers.map((crewMember) => {
          const isSelected = selectedCrewMember === crewMember.id;
          return (
            <button
              key={crewMember.id}
              onClick={() => onCrewMemberChange(crewMember.id)}
              className={`px-1 py-2 text-sm font-medium whitespace-nowrap transition-all border-b-2 flex items-center gap-2 ${
                isSelected
                  ? 'border-b-2'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
              }`}
              style={{
                color: isSelected ? crewMember.color : undefined,
                borderBottomColor: isSelected ? crewMember.color : undefined,
              }}
            >
              <span
                className="w-1 h-2 rounded-full"
                style={{ backgroundColor: crewMember.color }}
              />
              <span className="truncate">{crewMember.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Legend component unchanged (minor RTL-safe tweaks only if you want)
export function CrewMemberLegend({
  crewMembers,
  appointments = [],
  selectedDate = null,
  className = '',
}) {
  const getAppointmentCount = (crewMemberId) => {
    let filtered = appointments.filter(
      (apt) =>
        apt.crew_member_id === crewMemberId &&
        apt.status !== 'cancelled'
    );
    if (selectedDate) {
      filtered = filtered.filter((apt) => apt.date === selectedDate);
    }
    return filtered.length;
  };

  const getUpcomingCount = (crewMemberId) => {
    const now = new Date();
    return appointments.filter((apt) => {
      if (apt.crew_member_id !== crewMemberId || apt.status === 'cancelled') return false;
      const aptDateTime = new Date(`${apt.date}T${apt.time}`);
      return aptDateTime > now;
    }).length;
  };

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">Crew Members</h3>
        {selectedDate && (
          <span className="text-xs text-gray-500">
            {new Date(selectedDate).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {crewMembers.map((crewMember) => {
          const appointmentCount = getAppointmentCount(crewMember.id);
          const upcomingCount = getUpcomingCount(crewMember.id);

          return (
            <div key={crewMember.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <div className="flex items-center">
                <div
                  className="w-4 h-4 rounded-full me-3 flex-shrink-0"
                  style={{ backgroundColor: crewMember.color }}
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">{crewMember.name}</div>
                  <div className="text-xs text-gray-500">
                    {crewMember.specialties?.slice(0, 2).join(', ')}
                  </div>
                </div>
              </div>

              <div className="text-end">
                <div className="text-sm font-medium text-gray-900">{appointmentCount}</div>
                <div className="text-xs text-gray-500">
                  {selectedDate ? 'today' : `${upcomingCount} upcoming`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
